import { ReactNode, useEffect, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

import * as sessionService from '../../features/auth/services/session';

import Loader from './Loader';

export interface ProtectedRouteProps {
    children: ReactNode;
}

/**
 * ProtectedRoute Component
 * Wraps routes that require authentication.
 * Redirects unauthenticated users to login and preserves the intended destination.
 */
const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();
    const [isChecking, setIsChecking] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    useEffect(() => {
        const checkAuth = async () => {
            const authenticated = sessionService.isAuthenticated();

            if (!authenticated) {
                // Store the current path for post-login redirect in localStorage (matching AccountPage)
                localStorage.setItem('postLoginRedirect', location.pathname);

                // Navigate to the internal account page instead of directly to Ping Login
                // Determine the correct account path (event-aware or store-level)
                const { storeKey, eventKey } = params;
                const accountPath = storeKey 
                    ? (eventKey ? `/${storeKey}/${eventKey}/account` : `/${storeKey}/account`)
                    : '/account';
                    
                navigate(accountPath);
            } else {
                setIsAuthenticated(true);
            }

            setIsChecking(false);
        };

        checkAuth();
    }, [location.pathname, navigate, params]);

    // Show loader while checking authentication
    if (isChecking) {
        return <Loader text="Verifying authentication..." />;
    }

    // Render children only if authenticated
    return isAuthenticated ? <>{children}</> : null;
};

export default ProtectedRoute;
